import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { Mail, Loader2, Send } from "lucide-react";

export default function Contact({ config }) {
  const email = config?.contact_email || "info@katemercerbooks.com";
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-gray-200 px-5 py-3.5 text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all";

  return (
    <section id="contact" className="py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.8fr_1.2fr] gap-14 items-start">
        <div>
          <span className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">Contact</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-4">Get in Touch</h2>
          <p className="text-gray-600 mt-5 leading-relaxed">
            Whether you have a question about Kate's books, media enquiries, speaking opportunities or general
            feedback, we'd love to hear from you.
          </p>
          <a
            href={`mailto:${email}`}
            data-testid="contact-email-link"
            className="inline-flex items-center gap-3 mt-8 group"
          >
            <span className="h-12 w-12 rounded-2xl bg-[#0A0A0A] text-gold grid place-items-center group-hover:scale-105 transition-transform">
              <Mail size={20} />
            </span>
            <span className="font-medium border-b border-transparent group-hover:border-gold transition-colors">{email}</span>
          </a>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-white border border-gray-100 rounded-3xl p-7 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.05)]"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Name" value={form.name} onChange={update("name")} className={inputCls} data-testid="contact-name" />
            <input required type="email" placeholder="Email Address" value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-email-input" />
          </div>
          <input placeholder="Subject" value={form.subject} onChange={update("subject")} className={`${inputCls} mt-4`} data-testid="contact-subject" />
          <textarea required placeholder="Message" rows={5} value={form.message} onChange={update("message")} className={`${inputCls} mt-4 resize-none`} data-testid="contact-message" />
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="btn-gold rounded-full px-7 py-3.5 mt-6 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-70"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <><Send size={16} /> Send Message</>}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
